<script setup lang="ts">
import { ref, type Ref, watch } from 'vue'
import { currentLang } from '@/composables/settings/storage'
import { sendVerifyCode } from '@/composables/user/users'
import { checkMobile, VAL_MOBILE_INVALID } from '@/utils/validator'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { ElMessage } from 'element-plus'
import { renderMessage } from '@/utils/util'
import { useI18n } from 'vue-i18n'
import { initAliyunCaptcha, showAliyunCaptcha, isAliyunCaptchaReady } from '@/composables/captcha/aliyunCaptcha'
const { t } = useI18n()

// 倒计时60s

type SendStatus = 'default' | 'sending' | 'sent' | 'failed'

const props = defineProps({
  phone: {
    type: String,
    required: true,
    default: ''
  },
  type: {
    type: Number,
    default: 2,
    validator(value: number) {
      // 类型 1 - 注册, 2 - 验证码登录, 3 - 找回密码
      return [1, 2, 3].includes(value)
    }
  }
})

const phone2Send = ref(props.phone)

// 双向绑定phone值
watch(
  () => props.phone,
  (newVal) => {
    phone2Send.value = newVal
  }
)

const emit = defineEmits(['sendResult'])

let sendStatus: Ref<SendStatus> = ref('default')

let countDownSeconds = ref(60)

let disabled = ref(false)

onMounted(() => {
  initAliyunCaptcha()
})

function onSendCode() {
  const phoneValid = checkMobile(currentLang.value, phone2Send.value)

  if (phoneValid == VAL_MOBILE_INVALID) {
    ElMessage({
      message: renderMessage(t('validate.wrong_phone')),
      type: 'warning'
    })
    return
  }

  if (sendStatus.value !== 'default' && sendStatus.value !== 'failed') {
    return
  }

  const triggered = showAliyunCaptcha(
    (captchaVerifyParam) => {
      sendStatus.value = 'sending'
      sendCode(captchaVerifyParam)
    },
    () => {
      // 用户关闭验证码弹窗，保持按钮可点击状态
    }
  )
  if (!triggered) {
    // 验证码尚未就绪（重新初始化中或加载失败），降级直接发送
    sendStatus.value = 'sending'
    sendCode()
  }
}

// node timer:https://zhuanlan.zhihu.com/p/651465416
async function sendCode(captchaVerifyParam?: string) {
  const { data, error, statusCode } = await sendVerifyCode({
    phone: phone2Send.value,
    type: props.type,
    sceneId: 'ghzc3zjn',
    captchaVerifyParam
  })
  if (error.value) {
    emit('sendResult', 'failed')
    sendStatus.value = 'failed'
    return
  } else {
    const unpackData = data as Ref<CommonResponse<unknown>>
    if (unpackData.value.code == CODE_OK) {
      if (unpackData.value.data == true) {
        sendStatus.value = 'sent'
        countDownSeconds.value = 60
        startCountDown()
        disabled.value = true
      } else {
        sendStatus.value = 'default'
        countDownSeconds.value = 60
        disabled.value = false
        ElMessage({
          message: renderMessage(t('login.validation_failed')),
          type: 'warning'
        })
      }
    } else {
      sendStatus.value = 'default'
      countDownSeconds.value = 60
      disabled.value = false
      ElMessage({
        message: renderMessage(unpackData.value.message),
        type: 'warning'
      })
    }
  }
}

function startCountDown() {
  disabled.value = true
  const intervalId = window.setInterval(() => {
    countDownSeconds.value--
    if (countDownSeconds.value === 0) {
      stopCountDown(intervalId)
    }
  }, 1000)
}

function stopCountDown(intervalId: number) {
  window.clearInterval(intervalId)
  sendStatus.value = 'default'
  countDownSeconds.value = 60
  disabled.value = false
  emit('sendResult', 'success')
}
</script>
<template>
  <button
    :disabled="disabled"
    :class="{ 'cursor-not-allowed': disabled }"
    block
    min-w-36
    flex
    flex-row
    items-center
    justify-center
    text-center
    text-sm
    btn-outline
    @click.stop.prevent="onSendCode">
    <span v-if="sendStatus === 'default'">{{ $t('register.send_code') }}</span>
    <span v-if="sendStatus === 'sending'" i-ri:loader-2-fill inline-block preserve-3d animate-spin text-lg />
    <span v-if="sendStatus === 'sending'" ml-2>{{ $t('register.sending') }}</span>
    <span v-if="sendStatus === 'sent'">{{ $t('register.sent_count_down', { s: countDownSeconds }) }}</span>
    <span v-if="sendStatus === 'failed'" class="text-$c-danger">{{
      $t('register.sent_failed', { s: countDownSeconds })
    }}</span>
  </button>
</template>
<style lang="" scoped></style>
